import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faYoutube,
    faInstagram,
    faLinkedin,
    faFacebook,
    faXTwitter,
    faTiktok,
    faTelegram,
    faDiscord,
    faThreads,
    faGoogle,
} from '@fortawesome/free-brands-svg-icons';
import {
    Mail,
    Megaphone,
    FileText,
    Video,
    Magnet,
    Podcast,
    QrCode,
    Globe,
    Search,
    MousePointerClick,
    Monitor,
    Tablet,
    Smartphone,
} from 'lucide-react';
import './SourceLogo.css';

/* Maps a tracked resource_type to a label + icon key */
export const SOURCE_META = {
    video: { label: 'YouTube Video', icon: 'youtube' },
    pinComment: { label: 'Pinned Comment', icon: 'youtube' },
    channel: { label: 'Channel', icon: 'youtube' },
    community: { label: 'Community', icon: 'youtube' },
    live: { label: 'Live Stream', icon: 'youtube' },
    email: { label: 'Email', icon: 'email' },
    ad: { label: 'Ad', icon: 'ad' },
    blog: { label: 'Blog', icon: 'blog' },
    discord: { label: 'Discord', icon: 'discord' },
    facebook: { label: 'Facebook', icon: 'facebook' },
    instagram: { label: 'Instagram', icon: 'instagram' },
    leadMagnet: { label: 'Lead Magnet', icon: 'leadMagnet' },
    linkedin: { label: 'LinkedIn', icon: 'linkedin' },
    podcast: { label: 'Podcast', icon: 'podcast' },
    telegram: { label: 'Telegram', icon: 'telegram' },
    threads: { label: 'Threads', icon: 'threads' },
    tiktok: { label: 'TikTok', icon: 'tiktok' },
    twitter: { label: 'Twitter', icon: 'twitter' },
    webinar: { label: 'Webinar', icon: 'webinar' },
    website: { label: 'Website', icon: 'website' },
    qr: { label: 'QR Code', icon: 'qr' },
};

const YOUTUBE_TYPES = new Set(['video', 'pincomment', 'channel', 'community', 'live', 'youtube']);
const SOCIAL_TYPES = {
    instagram: { id: 'instagram', name: 'Instagram' },
    facebook: { id: 'facebook', name: 'Facebook' },
    twitter: { id: 'twitter', name: 'X' },
    x: { id: 'twitter', name: 'X' },
    linkedin: { id: 'linkedin', name: 'LinkedIn' },
    tiktok: { id: 'tiktok', name: 'TikTok' },
    threads: { id: 'threads', name: 'Threads' },
    discord: { id: 'discord', name: 'Discord' },
    telegram: { id: 'telegram', name: 'Telegram' },
};
const SOCIAL_HOSTS = {
    instagram: ['instagram.com', 'instagr.am'],
    facebook: ['facebook.com', 'fb.com'],
    twitter: ['twitter.com', 'x.com', 't.co'],
    linkedin: ['linkedin.com'],
    tiktok: ['tiktok.com'],
    threads: ['threads.net'],
    discord: ['discord.com', 'discord.gg'],
    telegram: ['t.me', 'telegram.org', 'telegram.me'],
};
const NAMED_SOURCES = {
    youtube: 'YouTube',
    instagram: 'Instagram',
    facebook: 'Facebook',
    twitter: 'X',
    linkedin: 'LinkedIn',
    tiktok: 'TikTok',
    threads: 'Threads',
    discord: 'Discord',
    telegram: 'Telegram',
    email: 'Email',
    podcast: 'Podcast',
    blog: 'Blog',
    ads: 'Ads',
    website: 'Website',
    webinar: 'Webinar',
    lead_magnet: 'Lead Magnet',
    google_search: 'Google Search',
    search: 'Search',
    direct: 'Direct',
    other: 'Other',
};
const SEARCH_HOST_MATCHERS = [
    'bing.com', 'duckduckgo.com', 'yahoo.', 'baidu.com', 'yandex.', 'ecosia.org',
];

const extractHostname = (referrer) => {
    if (!referrer || typeof referrer !== 'string') return '';
    const trimmed = referrer.trim();
    if (!trimmed) return '';
    try {
        const url = trimmed.includes('://') ? new URL(trimmed) : new URL(`https://${trimmed}`);
        return url.hostname.replace(/^www\./i, '').toLowerCase();
    } catch {
        return trimmed
            .toLowerCase()
            .replace(/^https?:\/\//, '')
            .replace(/^www\./, '')
            .split(/[/?#]/)[0] || '';
    }
};

const hostMatches = (host, suffix) => (
    host === suffix || host.endsWith(`.${suffix}`) || (suffix.endsWith('.') && host.includes(suffix))
);

const classifyByType = (type) => {
    const t = String(type || '').trim().toLowerCase();
    if (!t || t === 'null' || t === 'undefined' || t === 'na') return null;
    if (t === 'qr') return null;
    if (NAMED_SOURCES[t]) return { id: t, name: NAMED_SOURCES[t] };
    if (YOUTUBE_TYPES.has(t)) return { id: 'youtube', name: 'YouTube' };
    if (SOCIAL_TYPES[t]) return SOCIAL_TYPES[t];
    if (t === 'email') return { id: 'email', name: 'Email' };
    if (t === 'podcast') return { id: 'podcast', name: 'Podcast' };
    if (t === 'blog') return { id: 'blog', name: 'Blog' };
    if (t === 'ad' || t === 'ads') return { id: 'ads', name: 'Ads' };
    if (t === 'website') return { id: 'website', name: 'Website' };
    if (t === 'webinar') return { id: 'webinar', name: 'Webinar' };
    if (t === 'leadmagnet') return { id: 'lead_magnet', name: 'Lead Magnet' };
    return { id: 'other', name: 'Other' };
};

const classifyByReferrer = (referrer) => {
    const host = extractHostname(referrer);

    if (host.includes('youtube.com') || host === 'youtu.be') {
        return { id: 'youtube', name: 'YouTube' };
    }

    const social = Object.values(SOCIAL_TYPES).find((platform) => (
        (SOCIAL_HOSTS[platform.id] || []).some((suffix) => hostMatches(host, suffix))
    ));
    if (social) return social;

    if (
        host.startsWith('mail.')
        || host.includes('.mail.')
        || host.includes('mailchimp.com')
        || host.includes('sendgrid.net')
        || host.includes('klaviyo.com')
    ) {
        return { id: 'email', name: 'Email' };
    }

    if (host === 'google.com' || host.endsWith('.google.com') || host.includes('google.')) {
        return { id: 'google_search', name: 'Google Search' };
    }

    if (SEARCH_HOST_MATCHERS.some((matcher) => hostMatches(host, matcher) || host.includes(matcher))) {
        return { id: 'search', name: 'Search' };
    }

    if (!host) return { id: 'direct', name: 'Direct' };

    return { id: 'other', name: 'Other' };
};

/** Same grouping as Audience: resource type wins, otherwise classify from referrer. */
export const classifyTrafficSource = ({ referrer, type, qrSource } = {}) => {
    const t = String(type ?? '').trim();
    if (t && t.toLowerCase() !== 'null' && t.toLowerCase() !== 'undefined' && t.toLowerCase() !== 'na') {
        if (t.toLowerCase() === 'qr') {
            const sourceType = String(qrSource || '').trim();
            if (sourceType) {
                return classifyByType(sourceType) || { id: 'other', name: 'Other' };
            }
            return { id: 'qr', name: 'QR Code' };
        }
        return classifyByType(t) || { id: 'other', name: 'Other' };
    }
    return classifyByReferrer(referrer);
};

export const classifyResourceType = (type) => classifyTrafficSource({ type });

/* Maps a traffic source id (Audience traffic table) to an icon key */
export const TRAFFIC_SOURCE_ICON = {
    youtube: 'youtube',
    instagram: 'instagram',
    facebook: 'facebook',
    twitter: 'twitter',
    linkedin: 'linkedin',
    tiktok: 'tiktok',
    threads: 'threads',
    discord: 'discord',
    telegram: 'telegram',
    email: 'email',
    google_search: 'google',
    search: 'search',
    podcast: 'podcast',
    blog: 'blog',
    ads: 'ad',
    website: 'website',
    webinar: 'webinar',
    lead_magnet: 'leadMagnet',
    direct: 'direct',
    other: 'other',
    qr: 'qr',
    desktop: 'desktop',
    mobile: 'mobile',
    tablet: 'tablet',
    unknown: 'unknown',
};

const BrandIcon = ({ icon: faIcon }) => (
    <FontAwesomeIcon icon={faIcon} />
);

export const SOURCE_ICONS = {
    youtube: <BrandIcon icon={faYoutube} />,
    instagram: <BrandIcon icon={faInstagram} />,
    linkedin: <BrandIcon icon={faLinkedin} />,
    facebook: <BrandIcon icon={faFacebook} />,
    twitter: <BrandIcon icon={faXTwitter} />,
    tiktok: <BrandIcon icon={faTiktok} />,
    telegram: <BrandIcon icon={faTelegram} />,
    discord: <BrandIcon icon={faDiscord} />,
    threads: <BrandIcon icon={faThreads} />,
    google: <BrandIcon icon={faGoogle} />,
    email: <Mail size={13} />,
    ad: <Megaphone size={13} />,
    blog: <FileText size={13} />,
    leadMagnet: <Magnet size={13} />,
    podcast: <Podcast size={13} />,
    webinar: <Video size={13} />,
    website: <Globe size={13} />,
    qr: <QrCode size={13} />,
    search: <Search size={13} />,
    direct: <MousePointerClick size={13} />,
    other: <Globe size={13} />,
    desktop: <Monitor size={13} />,
    mobile: <Smartphone size={13} />,
    tablet: <Tablet size={13} />,
    unknown: <Globe size={13} />,
};

/* Maps a referrer hostname (e.g. "youtube.com") to an icon key */
const REFERRER_ICON_PATTERNS = [
    ['youtu', 'youtube'],
    ['instagram', 'instagram'],
    ['facebook', 'facebook'],
    ['fb.com', 'facebook'],
    ['linkedin', 'linkedin'],
    ['lnkd.in', 'linkedin'],
    ['twitter', 'twitter'],
    ['x.com', 'twitter'],
    ['t.co', 'twitter'],
    ['tiktok', 'tiktok'],
    ['threads', 'threads'],
    ['discord', 'discord'],
    ['t.me', 'telegram'],
    ['telegram', 'telegram'],
    ['google', 'google'],
    ['bing', 'search'],
    ['duckduckgo', 'search'],
    ['yahoo', 'search'],
];

export const getReferrerIconKey = (host) => {
    if (!host || host === 'direct') return 'direct';
    const value = String(host).toLowerCase();
    const match = REFERRER_ICON_PATTERNS.find(([pattern]) => value.includes(pattern));
    return match ? match[1] : 'website';
};

/* Low-level badge — renders a coloured square with the source icon */
export const SourceIconBadge = ({ iconKey, title, size, className = '' }) => (
    <span
        className={`source-logo source-logo--${iconKey}${size === 'sm' ? ' source-logo--sm' : ''}${className ? ` ${className}` : ''}`}
        title={title}
        aria-label={title}
    >
        {SOURCE_ICONS[iconKey] || SOURCE_ICONS.website}
    </span>
);

/* Journey first-touch source (Audience) */
export const SourceLogo = ({ source }) => {
    const meta = SOURCE_META[source] || { label: source || 'Unknown', icon: 'website' };
    return <SourceIconBadge iconKey={meta.icon} title={`First source: ${meta.label}`} />;
};

/* Traffic-sources table rows (Audience) */
export const TrafficSourceLogo = ({ sourceId, name }) => {
    const iconKey = TRAFFIC_SOURCE_ICON[sourceId] || 'other';
    return <SourceIconBadge iconKey={iconKey} title={name} />;
};

/* Tracked resource conversions (Activity) */
export const ResourceSourceLogo = ({ resourceType, size }) => {
    const meta = SOURCE_META[resourceType] || { label: resourceType || 'Unknown', icon: 'website' };
    return <SourceIconBadge iconKey={meta.icon} title={meta.label} size={size} />;
};

/* Referrer-only conversions (Activity) */
export const ReferrerSourceLogo = ({ host, size }) => (
    <SourceIconBadge iconKey={getReferrerIconKey(host)} title={host} size={size} />
);
